import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom, useAtomValue } from 'jotai';
import ReferrerField, {
  type Referrer,
  type UseUserSearch,
} from '@/components/Approval/@components/ReferrerField';
import { useUserSearch } from '@/components/Approval/@components/ReferrerField/hooks/useUserSearch';
import useDeactivatedUsers from '@/hooks/approval/useDeactivatedUsers';
import useFixedApprovalMembers from '@/hooks/approval/useFixedApprovalMembers';
import { useFormFixedReferrers } from '@/hooks/approval/useFormFixedMembers';
import { MyInfoState } from '@/recoil/atom';
import type { ApprovalTypeReferrer } from '@/types/approval';
import { pickMissingFixedMembers } from '@/utils/approval/fixedApprovalMembers';
import EyeIcon from '@/public/icons/approval/eye-24.svg';
import { SectionHint } from '@/components/Approval/@components/NewCorporateCard';
import {
  approvalCreateApprovalLineAtom,
  approvalCreateReferrersAtom,
} from './atoms';
import ResetTextButton from '@/components/Approval/@components/ResetTextButton';
import {
  SectionBox,
  SectionContent,
  SubSectionLabel,
  SubSectionLabelText,
} from './styles';

// 상신자 본인은 참조인 검색에서 제외
const useUserSearchExcludingMe: UseUserSearch = (keyword) => {
  const { passportId: myPassportId } = useAtomValue(MyInfoState);
  const { data, isLoading, error } = useUserSearch(keyword);
  const filtered = useMemo(
    () => (myPassportId ? data.filter((r) => r.id !== myPassportId) : data),
    [data, myPassportId],
  );
  return { data: filtered, isLoading, error };
};

type ReferrerSectionProps = {
  apvType?: number;
  /**
   * 'N' 신규 작성 / 'E' 편집.
   * 자동 적용은 신규 작성에서만 — 편집은 서버 저장값이 우선이라 없던 고정 참조인을 끼워넣지 않는다.
   * 다만 고정 여부 판별(삭제 잠금)은 양식 메타 기준이라 두 모드 모두 동작한다.
   */
  mode: 'N' | 'E';
};

const ReferrerSection = ({ apvType, mode }: ReferrerSectionProps) => {
  const { t } = useTranslation();
  // 최근 결재라인 적용 시 ApprovalLineSection이 직접 덮어쓰므로 로컬 state 대신 atom 사용.
  const [referrers, setReferrers] = useAtom(approvalCreateReferrersAtom);
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);

  const isNewMode = mode === 'N';
  // 작성자 법인 매칭은 BE 가 처리한 값이 내려온다. 작성자 본인 제외는 훅에서.
  const formFixed = useFormFixedReferrers(apvType);

  // 검색으로는 재직자만 들어오지만 최근 결재라인 적용이나 편집 모드 복원으로는
  // 퇴사자가 참조인에 들어올 수 있다. 그래서 어떤 경로로 왔든 현재 선택된
  // 참조인 전원의 재직 여부를 서버에 확인해 퇴사자 칩에 배지를 붙인다.
  // 고정 후보도 함께 조회한다 — 목록에 넣기 전에 퇴사 여부를 알아야 자동 추가를 막는다.
  const deactivationTargetIds = useMemo(
    () =>
      Array.from(
        new Set([...referrers.map((r) => r.id), ...formFixed.map((r) => r.id)]),
      ),
    [referrers, formFixed],
  );
  const { deactivatedIds, isResolved: deactivationResolved } =
    useDeactivatedUsers(deactivationTargetIds);
  const isDeactivated = useCallback(
    (r: Referrer) => deactivatedIds.has(r.id),
    [deactivatedIds],
  );

  /**
   * 퇴사자는 고정 대상에서 뺀다 — 자동 추가도, 삭제 잠금도 하지 않는다.
   * 퇴사자가 섞이면 CreateFooter 가 상신을 차단하는데, 잠겨 있거나 지워도 보정이 다시 넣으면
   * 빠져나갈 방법이 없어진다. 양식 메타는 BE 가 퇴사자를 걸러 내려주지만 그 기준과 여기
   * live 판정이 어긋날 수 있어 화면에서 막아둔다.
   */
  const fixed = useMemo(
    () => formFixed.filter((r) => !deactivatedIds.has(r.id)),
    [formFixed, deactivatedIds],
  );

  /**
   * 고정 참조인이 목록에 없으면 채워 넣는다. 이미 있으면 no-op(멱등).
   * deps 에 referrers 가 있어야 최근 결재라인 적용처럼 목록을 통째로 덮어쓴 뒤에도 다시 채워진다.
   */
  const ensureFixedReferrers = useCallback(
    (members: ApprovalTypeReferrer[]) => {
      if (
        pickMissingFixedMembers(members, new Set(referrers.map((r) => r.id)))
          .length === 0
      ) {
        return;
      }
      setReferrers((prev) => {
        const additions: Referrer[] = pickMissingFixedMembers(
          members,
          new Set(prev.map((r) => r.id)),
        ).map((r) => ({
          id: r.id,
          name: r.name,
          positionName: r.positionName,
          departmentName: r.departmentName,
          divisionName: r.divisionName,
        }));
        if (additions.length === 0) return prev;
        return [...prev, ...additions];
      });
    },
    [referrers, setReferrers],
  );

  // 판정이 도착하기 전에 보정을 돌리면 퇴사자가 잠깐 자동 추가된다(판정 후 풀리긴 하지만
  // 첫 진입에서 깜빡인다). 판정 도착까지 보정을 미룬다 — error 면 도착으로 쳐서 진행.
  const { isFixed } = useFixedApprovalMembers(
    fixed,
    isNewMode && deactivationResolved,
    ensureFixedReferrers,
  );

  // 퇴사자는 위 fixed 단계에서 이미 빠져 있어 잠기지 않는다.
  const isLocked = useCallback((r: Referrer) => isFixed(r.id), [isFixed]);

  // 실제 제외는 approvalFormToCreatePayload — 안내 조건을 같게 둔다
  const approvalLine = useAtomValue(approvalCreateApprovalLineAtom);
  const hasLineOverlap = useMemo(() => {
    const lineIds = new Set(
      [
        ...approvalLine.approvers,
        ...(approvalLine.cooperation?.members ?? []),
      ].map((m) => m.passportId),
    );
    return referrers.some((r) => lineIds.has(r.id));
  }, [approvalLine, referrers]);

  return (
    <SectionBox>
      <SubSectionLabel>
        <SubSectionLabelText>
          <EyeIcon width={20} height={20} />
          {t('참조인')}
        </SubSectionLabelText>
        {referrers.some((r) => !isLocked(r)) && (
          <ResetTextButton
            // 초기화는 사용자가 직접 추가한 참조인만 지운다 — 양식 고정 항목은 남는다
            onClick={() => setReferrers((prev) => prev.filter(isLocked))}
            aria-label={t('참조인 초기화')}
          />
        )}
      </SubSectionLabel>
      <SectionContent>
        <ReferrerField
          placeholder={t('참조인 이름·소속 검색')}
          selected={referrers}
          onChange={(next) => {
            setReferrers(next);
            setKeyword('');
          }}
          keyword={keyword}
          onKeywordChange={setKeyword}
          open={open}
          onOpenChange={setOpen}
          useUserSearch={useUserSearchExcludingMe}
          isDeactivated={isDeactivated}
          isLocked={isLocked}
          showReset={false}
          hideHeader
          hideFooter
        />
        {hasLineOverlap && (
          <SectionHint>
            {t('결재라인에 포함된 사람은 참조인에서 제외되어 저장됩니다.')}
          </SectionHint>
        )}
      </SectionContent>
    </SectionBox>
  );
};

export default ReferrerSection;
